import { TprecmAuthorizationService } from './../../../authorization/tprecm-authorization.service';
import { CgpConfirmationDialogService } from './../../../../../shared/cgp-confirmation-dialog/cgp-confirmation-dialog.service';
import { DataTableComponent } from './../../../../../shared/data-table/data-table.component';
import { EcmNecCompleteDateComponent } from './ecm-nec-complete-date/ecm-nec-complete-date.component';
import { EcmService } from './../../ecm.service';
import { BehaviorSubject } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { Component, EventEmitter, Inject, OnInit, ViewChild } from '@angular/core';
import { debounceTime, filter, finalize, mergeMap, take } from 'rxjs/operators';
import { ColumnDef } from 'src/app/shared/data-table/data-table.interface';
import { Router } from '@angular/router';

interface EcmNecTool {
  programNameYear: string;
  toolId: number;
  tprToolId: number;
  toolNo: string;
}

/**
 * Data sent to ECM NEC Reports dialog
 *
 * @interface EcmNecReportsData
 */
interface EcmNecReportsData {
  tool: EcmNecTool;
  readonly: boolean;
}


/**
 * Data provided by a given row in the table
 *
 * @interface EcmNecReportsRowData
 */
interface EcmNecReportsRowData {
  toolId: number;
  necLec: string;
  necCompleteDate: string;
}

/**
 * Dialog for showing and modifying the NEC information of a tool
 *
 * @export
 * @class EcmNecReportsComponent
 * @implements {OnInit}
 */
@Component({
  selector: 'ecm-nec-reports',
  templateUrl: './ecm-nec-reports.component.html',
  styleUrls: ['./ecm-nec-reports.component.scss']
})
export class EcmNecReportsComponent implements OnInit {

  @ViewChild('necGrid') private readonly grid: DataTableComponent;

  tool: EcmNecTool;
  readonly: boolean;
  resourced = false;

  necData$: BehaviorSubject<any[]> = new BehaviorSubject([]);
  refresh$: EventEmitter<void> = new EventEmitter();

  loading = false;
  hasLoaded = false;

  columns: ColumnDef[] = [
    { field: 'necLec', name: 'NEC LEC', editable: false, width: '6em' },
    { field: 'necCompleteDate', name: 'NEC Compl Date', type: 'date', editable: false, tooltip: 'Notification Engineering Change Complete Date', width: '6em' },
    {
      field: 'editDate', name: 'Edit Nec Complete Date', type: 'icon-button', tooltip: 'Click this button to edit the Nec Complete Date.',
      icon: 'create', sortingDisabled: true, width: '6em',
      callback: this.editNecCompleteDate.bind(this)
    }
  ];
  necDatas: unknown;
  selectedNecLec: any;

  constructor(
    private readonly router: Router,
    private readonly dialogRef: MatDialogRef<EcmNecReportsComponent>,
    @Inject(MAT_DIALOG_DATA) public data: EcmNecReportsData,
    private readonly confirmation: CgpConfirmationDialogService,
    private readonly ecmService: EcmService,
    private readonly toastr: ToastrService,
    private readonly auth: TprecmAuthorizationService,
    private readonly dialog: MatDialog
  ) { }

  ngOnInit() {

    if (this.router.url === '/tprecm/ecm/re-source') {
      this.resourced = true;
      this.columns.forEach(val => { val.editable = false; val.headerHighlight = false; });
      this.columns.forEach(val => {
        if (val.field === 'necLec') {
          return (val.type = 'readonly');
        }
      });
    }

    if (!this.validateDialogData()) {
      this.dialogRef.close();
      return;
    }

    // Setup local values
    this.tool = this.data.tool;
    this.readonly = this.data.readonly;

    // Update NEC Data
    this.refresh$.pipe(
      debounceTime(1000)
    ).subscribe(this.updateNecData.bind(this));

    this.refresh$.emit();

    if (this.readonly) {
      this.columns.forEach(col => col.editable = false);
    }

    this.hasLoaded = true;
  }

  /** CLASS FUNCTIONS */

  /**
   * Validate dialogs incoming data
   */
  validateDialogData(): boolean {
    return this.data.tool.toolId != null;
  }

  /** UI FUNCTIONS */

  /**
   * Update the NEC Report data
   */
  updateNecData(necLec?: any): void {
    this.loading = true;
    this.ecmService.getEcmNec(this.tool.toolId)
      .pipe(
        filter(Boolean),
        finalize(() => this.loading = false)
      ).subscribe(data => {
        this.necData$.next(data instanceof Array ? data : []);
        if (necLec) {
          this.grid.selections = [this.necData$.value.find(el => el.necLec === necLec)];
          this.selectedNecLec = this.grid.selections[0];
        }
      });
  }

  /**
   * Open a dialog to edit a given row NEC Complete Date
   *
   * @param {EcmNecReportsRowData} rowData
   * @memberof EcmNecReportsComponent
   */
  editNecCompleteDate(rowData: EcmNecReportsRowData): void {
    const toolId = this.tool.toolId;

    const dialogRef = this.dialog.open(EcmNecCompleteDateComponent, {
      disableClose: true,
      data: { ...rowData, toolId, toolNo: this.tool.toolNo }
    });

    dialogRef.afterClosed()
      .pipe(
        filter(Boolean)
      ).subscribe(() => this.printReport(rowData, toolId, 'Update was successful!  Do you want to save this NEC file to the NEC folder?'));
  }

  printReport(data: EcmNecReportsRowData, toolId: number, actionString: string): any {
    const reportTag = data.necCompleteDate ? 'NEC' : 'IN_PROCESS';
    this.auth.userRoleId()
      .pipe(take(1))
      .subscribe((roleId) => {
        if (roleId === 9) {
          this.ecmService.printNecReport(toolId, data.necLec, false, this.tool.tprToolId, this.tool.toolNo, this.tool.programNameYear, reportTag);
          this.refresh(data.necLec);
        }
        else {
          this.confirmation.showConfirmationDialog({
            title: 'Save  File to NEC Folder',
            cancelButton: true,
            message: actionString,
            disableClose: false,
            onYesClick: () => {
              if (!this.resourced) {
                this.ecmService.printNecReport(toolId, data.necLec, true, this.tool.tprToolId, this.tool.toolNo, this.tool.programNameYear, reportTag);
                this.refresh(data.necLec);
                this.confirmation.closeConfirmationDialog();
              }
            },
            onNoClick: () => {
              this.ecmService.printNecReport(toolId, data.necLec, false, this.tool.tprToolId, this.tool.toolNo, this.tool.programNameYear, reportTag);
              this.refresh(data.necLec);
              this.confirmation.closeConfirmationDialog();
            }
          }).afterClosed().subscribe(() => this.refresh(data.necLec));
        }

      });
  }

  /**
   * Prints the NEC Report to a file
   *
   * @memberof EcmNecReportsComponent
   */
  onReportclick() {
    const selected = this.grid.selected;

    if (!selected) {
      this.toastr.error('Please select a row to print NEC report for.');
      return;
    }

    this.printReport(selected, this.tool.toolId, 'Do you want to save this file to the NEC folder?');
  }

  refresh(necLec?: any): void {
    this.refresh$.emit(necLec);
  }

  necLecSelected(selected: any) {
    this.selectedNecLec = selected;
  }

  /**
   * Cancel current dialog and close it
   *
   * @memberof EcmNecReportsComponent
   */
  cancel(): void {
    this.dialogRef.close();
  }
}
